import { useState } from "react";
import PropTypes from "prop-types";
import { QIModal, QIModalHeader, QIModalBody } from "../../../../components";
import { SvgIcon } from "../../../Shared";
import { getLocalizedString } from "../../../../shared/translation";
import { filterVariables } from "./eventEmailUtils";

/**
 * The "Available variables" modal for the Event Email Delivery card.
 *
 * Presents system and client variables with their template tokens and source.
 * Each row is available through a case-insensitive substring search and
 * copies its token to the clipboard, showing a per-row confirmation.
 *
 * The list is derived purely from `filterVariables(variables, search)` so the
 * search/no-match behavior stays consistent with the tested helper surface.
 *
 * Requirements: 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8
 *
 * @param {boolean} show whether the modal is visible
 * @param {func} onHide called when the modal requests to close
 * @param {Array<{ name: string, token: string, source: string }>} variables the variable list
 */
export const AvailableVariablesModal = ({
  show,
  onHide,
  variables = [],
  loading = false,
  loadError = false,
}) => {
  const [search, setSearch] = useState("");
  // Token whose "Copied" confirmation is currently visible.
  const [copiedToken, setCopiedToken] = useState(null);
  // Token whose copy attempt failed.
  const [failedToken, setFailedToken] = useState(null);

  const filtered = filterVariables(variables, search);
  const handleCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setFailedToken(null);
      setCopiedToken(token);
      setTimeout(() => {
        setCopiedToken((current) => (current === token ? null : current));
      }, 3000);
    } catch (error) {
      // Clipboard write rejected: surface a failure indication and leave the
      // clipboard contents unchanged (nothing was written).
      setCopiedToken(null);
      setFailedToken(token);
    }
  };

  const actionTitle = getLocalizedString("copy", "Copy");

  return (
    <QIModal show={show} onHide={onHide} size="medium">
      <QIModalHeader onHide={onHide} closeButton>
        {getLocalizedString("available_variables", "Available Variables")}
      </QIModalHeader>
      <QIModalBody>
        <div className="available-variables">
          <p className="available-variables_hint">
            {getLocalizedString(
              "copy_variable_hint",
              "Copy a variable template and paste it into your message or subject field."
            )}
          </p>

          <div className="available-variables_search-wrapper">
            <SvgIcon name="search" wrapperClass="available-variables_search-icon" />
            <input
              type="text"
              className="available-variables_search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={getLocalizedString(
                "search_variables",
                "Search variables..."
              )}
              aria-label={getLocalizedString(
                "search_variables",
                "Search variables"
              )}
            />
          </div>

          {loadError && variables.length > 0 && (
            <p className="available-variables_empty text-danger">
              {getLocalizedString("variables_load_error", "Could not load some variables.")}
            </p>
          )}
          {loading && variables.length === 0 ? (
            <p className="available-variables_empty">{getLocalizedString("loading", "Loading")}...</p>
          ) : loadError && variables.length === 0 ? (
            <p className="available-variables_empty text-danger">
              {getLocalizedString("variables_load_error", "Could not load variables.")}
            </p>
          ) : filtered.length === 0 ? (
            <p className="available-variables_empty">
              {getLocalizedString(
                "no_matching_variables",
                "No matching variables found"
              )}
            </p>
          ) : (
            <div className="available-variables_table-wrapper">
              <table className="available-variables_table">
                <thead>
                  <tr>
                    <th>{getLocalizedString("variable", "Variable")}</th>
                    <th>{getLocalizedString("template", "Template")}</th>
                    <th>{getLocalizedString("source", "Source")}</th>
                    <th aria-label={actionTitle} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((variable) => (
                    <tr key={`${variable.source}-${variable.name}`}>
                      <td className="available-variables_name">
                        {variable.name}
                      </td>
                      <td>
                        <code className="available-variables_token">
                          {variable.token}
                        </code>
                      </td>
                      <td>{getLocalizedString(variable.source.toLowerCase(), variable.source)}</td>
                      <td className="available-variables_action-cell">
                        <button
                          type="button"
                          className="available-variables_copy"
                          style={{ border: 0, background: "transparent" }}
                          onClick={() => handleCopy(variable.token)}
                          aria-label={`${actionTitle} ${variable.token}`}
                        >
                          <SvgIcon name="copy" />
                        </button>
                        {copiedToken === variable.token && (
                          <span className="available-variables_copied" role="status">
                            {getLocalizedString("copied", "Copied")}
                          </span>
                        )}
                        {failedToken === variable.token && (
                          <span className="available-variables_copy-failed" role="status">
                            {getLocalizedString("copy_failed", "Copy failed")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </QIModalBody>
    </QIModal>
  );
};

AvailableVariablesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      token: PropTypes.string.isRequired,
      source: PropTypes.string.isRequired,
    })
  ),
  loading: PropTypes.bool,
  loadError: PropTypes.bool,
};
